import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Infinity as InfinityIcon, Headset, Cpu, Wallet, Package, KeyRound, Zap, MessageCircle, Instagram, Users, Hash, Globe2 } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';
import { LineReveal, FadeUp, Stagger, staggerItem } from '../components/Reveal';
import BrowserFrame from '../components/BrowserFrame';
import Marquee from '../components/Marquee';
import GyenoMark from '../components/GyenoMark';
import ChannelLogo from '../components/ChannelLogo';
import { StudioHomeMock, BuilderCanvasMock, TestConsoleMock } from '../components/StudioMock';

const featureIcons = [InfinityIcon, Headset, Cpu, Wallet, Package, KeyRound];

// Channels Gyeno agents can deploy to. Same list drives both the marquee
// (brand logo + name) and the icon grid below it.
const channels = [
  { icon: MessageCircle, type: 'whatsapp', name: 'WhatsApp' },
  { icon: Instagram, type: 'instagram', name: 'Instagram' },
  { icon: Users, type: 'teams', name: 'Microsoft Teams' },
  { icon: Hash, type: 'slack', name: 'Slack' },
  { icon: Globe2, type: 'web', name: 'Web Widget' },
];

// A single moving item inside the channel marquee: brand logo + name.
function ChannelMarqueeItem({ type, name }) {
  return (
    <span className="inline-flex items-center gap-3 text-lg sm:text-xl font-semibold tracking-tight normal-case text-white/70">
      <ChannelLogo type={type} className="h-7 w-7 sm:h-8 sm:w-8 shrink-0" />
      {name}
    </span>
  );
}

export default function Home() {
  const { t } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const shotY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const shotRotate = useTransform(scrollYProgress, [0, 1], [2.5, -1.5]);
  const glowOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div>
      {/* ===== HERO ===== */}
      <section ref={heroRef} className="relative bg-[#10282C] grain overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <motion.div style={{ opacity: glowOpacity }} className="absolute -top-40 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full bg-[#00C5C2]/10 blur-[140px] pointer-events-none" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-8 lg:pt-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-6">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="inline-flex items-center gap-2.5 px-4 py-3 rounded-full border border-[#00C5C2]/40 bg-[#00C5C2]/[0.08] shadow-[0_8px_24px_-12px_rgba(0,197,194,0.55)]"
                data-testid="hero-badge"
              >
                <GyenoMark size={22} onDark interactive={false} />
                <span className="text-[14px] font-mono tracking-wide text-[#00C5C2] font-semibold">
  {t('hero.badge')}
</span>
              </motion.div>

              <h1 className="mt-7 font-display font-bold text-white text-4xl sm:text-5xl lg:text-[3.6rem] tracking-tight leading-[1.08]" data-testid="hero-headline">
                <LineReveal
                  delay={0.15}
                  lines={[t('hero.line1'), t('hero.line2')]}
                />
                <span className="block overflow-hidden">
                  <motion.span
                    className="block text-[#00C5C2]"
                    initial={{ y: '110%' }}
                    animate={{ y: '0%' }}
                    transition={{ duration: 0.9, delay: 0.39, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {t('hero.line3')}
                  </motion.span>
                </span>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="mt-6 text-base md:text-lg text-white/60 leading-relaxed max-w-xl"
              >
                {t('hero.sub')}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="mt-9 flex flex-col sm:flex-row gap-3.5"
              >
                <Link to="/signup" data-testid="hero-cta-primary" className="btn-coral inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-[15px] font-semibold">
                  {t('hero.ctaPrimary')} <ArrowRight size={16} />
                </Link>
                <Link to="/enterprise" data-testid="hero-cta-secondary" className="btn-ghost-dark inline-flex items-center justify-center px-7 py-3.5 rounded-full text-[15px] font-medium">
                  {t('hero.ctaSecondary')}
                </Link>
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.9 }}
                className="mt-8 text-[12px] font-mono tracking-wide text-white/35"
              >
                {t('trust.regions')}
              </motion.p>
            </div>

            <div className="lg:col-span-6 relative">
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                <motion.div style={{ y: shotY, rotate: shotRotate }}>
                  <BrowserFrame url="yourorg.lightning.force.com/gyeno/studio">
                    <StudioHomeMock />
                  </BrowserFrame>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute -bottom-6 -left-3 sm:-left-8 flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-white shadow-[0_16px_40px_-10px_rgba(16,40,44,0.4)] border border-[#10282C]/8"
                  data-testid="hero-thinking-chip"
                >
                  <GyenoMark size={22} thinking interactive={false} />
                  <span className="text-[12px] font-medium text-[#10282C]/75">{t('hero.thinking')}</span>
                </motion.div>
              </motion.div>
              <p className="mt-14 text-right text-[11px] font-mono text-white/30">{t('hero.caption')}</p>
            </div>
          </div>
        </div>

        {/* trust strip */}
        <div className="relative z-10 border-t border-white/8">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 flex flex-wrap items-center justify-center lg:justify-between gap-x-10 gap-y-3">
            <span className="text-[12.5px] font-mono tracking-wide text-white/45 flex items-center gap-2">
              <Zap size={13} className="text-[#00C5C2]" /> {t('trust.builtFor')}
            </span>
            <span className="text-[12.5px] font-mono tracking-wide text-white/45">{t('trust.native')}</span>
            <span className="text-[12.5px] font-mono tracking-wide text-white/45">{t('trust.byDulip')}</span>
          </div>
        </div>
      </section>

      {/* ===== MANIFESTO ===== */}
      <section className="py-28 sm:py-36 bg-[#F6F8F7]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="text-[18px] font-semibold underline underline-offset-8 decoration-2 text-[#00918F]">{t('manifesto.kicker')}</p>
          </FadeUp>
          <div className="mt-14 space-y-20">
            {[1, 2, 3].map((n) => (
              <FadeUp key={n} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start border-t border-[#10282C]/10 pt-10">
                <span className="md:col-span-2 font-display text-5xl sm:text-6xl font-bold text-[#10282C]/12">0{n}</span>
                <h2 className="md:col-span-4 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#10282C] leading-snug">
                  {t(`manifesto.ch${n}t`)}
                </h2>
                <p className="md:col-span-6 text-base text-[#10282C]/60 leading-relaxed max-w-xl">
                  {t(`manifesto.ch${n}b`)}
                </p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-28 sm:py-36 bg-white border-y border-[#10282C]/6">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="eyebrow text-[18px] font-semibold underline underline-offset-8 decoration-2 text-[#00918F]">{t('how.kicker')}</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#10282C] max-w-2xl leading-snug">
              {t('how.title')}
            </h2>
          </FadeUp>
          <div className="mt-16 space-y-24">
            {[
              { n: 1, mock: <StudioHomeMock />, url: 'gyeno/studio' },
              { n: 2, mock: <BuilderCanvasMock />, url: 'gyeno/studio/agent/sales-coach' },
              { n: 3, mock: <TestConsoleMock />, url: 'gyeno/studio/test-console' },
            ].map(({ n, mock, url }, i) => (
              <FadeUp key={n} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center`}>
                <div className={`lg:col-span-5 ${i % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#10282C] text-[#00C5C2] font-display font-bold text-lg">{n}</span>
                  <h3 className="mt-5 font-display text-xl sm:text-2xl font-semibold tracking-tight text-[#10282C]">{t(`how.s${n}t`)}</h3>
                  <p className="mt-3.5 text-base text-[#10282C]/60 leading-relaxed">{t(`how.s${n}b`)}</p>
                </div>
                <div className={`lg:col-span-7 ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <BrowserFrame url={`yourorg.lightning.force.com/${url}`} className={n === 3 ? 'max-w-md mx-auto' : ''}>
                    {mock}
                  </BrowserFrame>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp className="mt-16 text-center">
            <Link to="/how-it-works" data-testid="home-how-cta" className="inline-flex items-center gap-2 link-sweep text-[#00918F] font-semibold text-[15px]">
              {t('how.cta')} <ArrowRight size={15} />
            </Link>
          </FadeUp>
        </div>
      </section>

        {/* ===== FEATURES ===== */}
      <section className="py-28 sm:py-36 bg-[#F6F8F7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="eyebrow text-[18px] font-semibold underline underline-offset-8 decoration-2 text-[#00918F]">{t('features.kicker')}</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#10282C] max-w-2xl leading-snug">
              {t('features.title')}
            </h2>
          </FadeUp>
          <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureIcons.map((Icon, i) => (
              <motion.div
                key={i}
                variants={staggerItem}
                data-testid={`feature-card-${i + 1}`}
                className="group rounded-2xl bg-white border border-[#10282C]/8 p-7 hover:border-[#00918F]/35 hover:shadow-[0_20px_50px_-18px_rgba(0,145,143,0.25)]"
                style={{ transition: 'border-color 0.3s ease, box-shadow 0.3s ease' }}
              >
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#00918F]/10 text-[#00918F] group-hover:bg-[#00918F] group-hover:text-white" style={{ transition: 'background-color 0.3s ease, color 0.3s ease' }}>
                  <Icon size={20} strokeWidth={1.8} />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-[#10282C]">{t(`features.f${i + 1}t`)}</h3>
                <p className="mt-2.5 text-sm text-[#10282C]/60 leading-relaxed">{t(`features.f${i + 1}b`)}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ===== DEPLOY ANYWHERE / CHANNELS ===== */}
      <section className="py-20 bg-[#10282C] grain overflow-hidden">
        <div className="relative z-10">
          <FadeUp className="max-w-7xl mx-auto px-5 sm:px-8">
            <p className="eyebrow text-[18px] font-semibold underline underline-offset-8 decoration-2 text-[#00C5C2]">{t('channels.kicker')}</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white max-w-xl leading-snug">
              {t('channels.title')}
            </h2>
          </FadeUp>
          <div className="mt-10">
            <Marquee
              dark
              items={channels.map((c) => (
                <ChannelMarqueeItem key={c.name} type={c.type} name={c.name} />
              ))}
            />
          </div>
          <FadeUp className="max-w-7xl mx-auto px-5 sm:px-8 mt-8">
            <Stagger className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
              {channels.map(({ icon: Icon, name }) => (
                <motion.div
                  key={name}
                  variants={staggerItem}
                  className="rounded-xl border border-white/10 bg-white/4 p-4 text-center"
                >
                  <Icon size={20} className="mx-auto text-white/60" strokeWidth={1.7} />
                  <p className="mt-2 text-[12px] font-medium text-white/70">{name}</p>
                </motion.div>
              ))}
            </Stagger>
          </FadeUp>
        </div>
      </section>

      {/* ===== PRICING TEASER ===== */}
      <section className="py-28 sm:py-32 bg-white border-b border-[#10282C]/6">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <FadeUp className="rounded-3xl bg-[#F6F8F7] border border-[#10282C]/8 p-10 sm:p-14 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 dot-grid-light opacity-70" />
            <div className="relative">
              <p className="font-display text-4xl sm:text-5xl font-bold text-[#10282C]">$49<span className="text-lg font-medium text-[#10282C]/40"> · one-time minimum</span></p>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#10282C] leading-snug">{t('pricingTeaser.title')}</h2>
              <p className="mt-3.5 text-base text-[#10282C]/60 leading-relaxed max-w-lg">{t('pricingTeaser.sub')}</p>
              <Link to="/pricing" data-testid="home-pricing-cta" className="mt-7 btn-teal inline-flex items-center gap-2 px-6 py-3 rounded-full text-[14px] font-semibold">
                {t('pricingTeaser.cta')} <ArrowRight size={15} />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ===== SOCIAL PROOF PLACEHOLDER ===== */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="eyebrow text-[18px] font-semibold underline underline-offset-8 decoration-2 text-[#00918F] text-center">{t('proof.kicker')}</p>
          </FadeUp>
          <Stagger className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                variants={staggerItem}
                data-testid={`logo-placeholder-${i + 1}`}
                className="h-16 rounded-xl border border-dashed border-[#10282C]/15 flex items-center justify-center"
              >
                <span className="text-[10px] font-mono text-[#10282C]/30">{t('proof.placeholder')}</span>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ===== ENTERPRISE CALLOUT ===== */}
      <section className="py-28 sm:py-32 bg-[#10282C] grain relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 text-left sm:text-center">
          <FadeUp>
            <span className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full border border-[#00C5C2]/40 bg-[#00C5C2]/[0.06] text-[14px] font-semibold font-mono tracking-wide text-[#00C5C2] shadow-[0_8px_24px_-12px_rgba(0,197,194,0.45)]">
  <KeyRound size={18} strokeWidth={2.2} /> BYOK · ENTERPRISE
</span>
            <h2 className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white leading-snug">{t('entCallout.title')}</h2>
            <p className="mt-4 text-base text-white/55 leading-relaxed max-w-xl sm:mx-auto">{t('entCallout.sub')}</p>
            <Link to="/enterprise" data-testid="home-enterprise-cta" className="mt-8 btn-coral inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-[15px] font-semibold">
              {t('entCallout.cta')} <ArrowRight size={16} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}